import type { GetApiV1RefereeMatchesTodayParams } from '@1webit/cvs-api-scheme/models';
import { queryOptions } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

import {
  refereeStateToColor,
} from '@/app/(dashboard)/referee/matches/[matchId]/_components/form/referee-match-detail-form-color-helper';
import type { Option } from '@/components/ui/select-react/select.interface';
import { DEFAULT_TABLE_PAGE_SIZE, DEFAULT_TABLE_PAGE_SIZE_HIGH, MAX_API_LIMIT } from '@/constants';
import { getCurrentSeasonString, getResponse, queryParams } from '@/utils';

// matches
export const getRefereeMatchesQueryOptions = (userId: number, season = getCurrentSeasonString(), page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, sort?: string) => {
  return queryOptions({
    queryKey: ['getApiV1RefereeMatches', userId, season, page, limit, sort],
    queryFn: async () => {
      const res = await getResponse('getApiV1RefereeMatches', {
        userId,
        season,
        ...queryParams({ limit, offset: (page - 1) * limit, sort }),
      });
      return {
        ...res,
        responseData: res.responseData.map(item => ({
          ...item,
          id: item.matchId,
        })),
      };
    },
  });
};

export const getApiV1RefereeMatchMatchId = (id: number) => {
  return queryOptions({
    queryKey: ['getApiV1RefereeMatchMatchId', id],
    queryFn: () => getResponse('getApiV1RefereeMatchMatchId', id),
  });
};
export const getRefereeMatchAssigneesQueryOptions = (matchId: number, listId: number) =>
  queryOptions({
    queryKey: ['getApiV1RefereeMatchListRefereesMatchIdListId', matchId, listId],
    queryFn: async () => {
      const res = await getResponse(
        'getApiV1RefereeMatchListRefereesMatchIdListId',
        matchId,
        listId,
      );
      return res.map(r => ({
        value: r.refereeId,
        label: `${r.text}`,
        color: refereeStateToColor(r),
      } as Option<number, { color?: string }>));
    },
  });
// calendar
export const getRefereeIdCalendarQueryOptions = (userId: number, from?: Dayjs | Date, to?: Dayjs | Date) => {
  if (from === undefined) {
    from = dayjs().startOf('month');
  } else if (from instanceof Date) {
    from = dayjs(from);
  }
  if (to === undefined) {
    to = dayjs(from).endOf('month');
  } else if (to instanceof Date) {
    to = dayjs(to);
  }

  const dateFrom = from.format('YYYY-MM-DD');
  const dateTo = to.format('YYYY-MM-DD');

  return queryOptions({
    queryKey: ['getApiV1RefereeUserIdCalendar', userId, dateFrom, dateTo],
    queryFn: () => getResponse('getApiV1RefereeUserIdCalendar', userId, { dateFrom, dateTo }),

  });
};

// unavailability
export const getApiV1UnavailabilitySeason = (userId: number, season = getCurrentSeasonString(), page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, sort?: string) => {
  return queryOptions({
    queryKey: ['getApiV1UnavailabilitySeason', userId, season, page, limit, sort],
    queryFn: () => getResponse('getApiV1UnavailabilitySeason', queryParams({
      userId,
      season,
      limit,
      offset: (page - 1) * limit,
      sort,
    })),
  });
};

export const getSeasonUnavailabilityStatisticsQueryOptions = (userId: number, season = getCurrentSeasonString()) => {
  return queryOptions({
    queryKey: ['getApiV1UnavailabilitySeasonStatistics', userId, season],
    queryFn: () => {
      return getResponse('getApiV1UnavailabilitySeasonStatistics', {
        userId,
        season,
      });
    },
  });
};

// excused
export const getApiV1UnavailabilitySeasonMatch = (userId: number, season = getCurrentSeasonString(), page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, sort?: string) => {
  return queryOptions({
    queryKey: ['getApiV1UnavailabilitySeasonMatch', userId, season, page, limit, sort],
    queryFn: () => getResponse('getApiV1UnavailabilitySeasonMatch', {
      userId,
      season,
      limit,
      offset: (page - 1) * limit,
      sort,
    }),
  });
};

export const getApiV1UnavailabilitySeasonMatchStatistics = (userId: number, season = getCurrentSeasonString()) => {
  return queryOptions({
    queryKey: ['getApiV1UnavailabilitySeasonMatchStatistics', userId, season],
    queryFn: () => {
      return getResponse('getApiV1UnavailabilitySeasonMatchStatistics', {
        userId,
        season,
      });
    },
  });
};

// detail
export const getRefereeIdDetailQueryOptions = (userId: number) => {
  return queryOptions({
    queryKey: ['getApiV1RefereeUserIdDetail', userId],
    queryFn: () => getResponse('getApiV1RefereeUserIdDetail', userId),
  });
};

// qualifications
export const getApiV1RefereeQualificationUserId = (userId: number) => {
  return queryOptions({
    queryKey: ['getApiV1RefereeQualificationUserId', userId],
    queryFn: () => getResponse('getApiV1RefereeQualificationUserId', userId),
  });
};

// colleagues
export const getPreferredColleaguesQueryOptions = (userId: number) => {
  return queryOptions({
    queryKey: ['getApiV1RefereeUserIdPreferredColleagues', userId],
    queryFn: () => getResponse('getApiV1RefereeUserIdPreferredColleagues', userId),
  });
};

// trainings
export const getRefereeTrainingsQueryOptions = (userId: number) => {
  return queryOptions({
    queryKey: ['getApiV1RefereeTrainingUserId', userId],
    queryFn: () => getResponse('getApiV1RefereeTrainingUserId', userId),
  });
};

// list
export const getRefereeIdListQueryOptions = (userId: number, history = false) => {
  return queryOptions({
    queryKey: ['getApiV1RefereeUserIdList', userId, history],
    queryFn: () => getResponse('getApiV1RefereeUserIdList', userId, { history }),
  });
};

export const getRefereeListQueryOptions = (search?: string, limit = DEFAULT_TABLE_PAGE_SIZE, page = 1, sort?: string) => {
  const params = queryParams({ search, limit, offset: (page - 1) * limit, sort });

  return queryOptions({
    queryKey: ['getApiV1RefereeRegistry', params],
    queryFn: () => getResponse('getApiV1RefereeRegistry', params),
  });
};

// delegations
export const getRefereeDelationGroupPermissionQueryOptions = (search?: string, limit = DEFAULT_TABLE_PAGE_SIZE_HIGH, page = 1, sort?: string) => {
  const params = queryParams({ search, limit, offset: (page - 1) * limit, sort });

  return queryOptions({
    queryKey: ['getApiV1DelegationGroupPermission', params],
    queryFn: () => getResponse('getApiV1DelegationGroupPermission', params),
  });
};
export const getRefereeDelationGroupFavoriteQueryOptions = (search?: string, limit = DEFAULT_TABLE_PAGE_SIZE_HIGH, page = 1, sort?: string) => {
  const params = queryParams({ search, limit, offset: (page - 1) * limit, sort });

  return queryOptions({
    queryKey: ['getApiV1DelegationGroupFavorite', params],
    queryFn: () => getResponse('getApiV1DelegationGroupFavorite', params),
  });
};
export const getRefereeDelagationGroupMatchesQueryOptions = (groupId: number, search?: string, limit = DEFAULT_TABLE_PAGE_SIZE, page = 1, sort?: string) => {
  const params = queryParams({ search, limit, offset: (page - 1) * limit, sort });

  return queryOptions({
    queryKey: ['getApiV1DelegationGroupGroupIdMatch', groupId, params],
    queryFn: async () => {
      const res = await getResponse('getApiV1DelegationGroupGroupIdMatch', groupId, params);
      return {
        ...res,
        responseData: res.responseData.map(item => ({
          ...item,
          id: item.matchId,
        })),
      };
    },
  });
};
export const getRefereeDelegationGroupIsFavorite = (groupId: number) => {
  return queryOptions({
    queryKey: ['getApiV1DelegationGroupGroupIdIsFavorite', groupId],
    queryFn: () => getResponse('getApiV1DelegationGroupGroupIdIsFavorite', groupId),
  });
};
export const getDelegationMatchVacant = (search?: string, limit = DEFAULT_TABLE_PAGE_SIZE, page = 1, sort?: string) => {
  const params = queryParams({ search, limit, offset: (page - 1) * limit, sort });

  return queryOptions({
    queryKey: ['getApiV1DelegationMatchVacant', params],
    queryFn: async () => {
      const res = await getResponse('getApiV1DelegationMatchVacant', params);
      return {
        ...res,
        responseData: res.responseData.map(item => ({
          ...item,
          id: item.matchId,
        })),
      };
    },
  });
};
export const getDelegationMatchVacantMaster = (search?: string, limit = DEFAULT_TABLE_PAGE_SIZE, page = 1, sort?: string) => {
  const params = queryParams({ search, limit, offset: (page - 1) * limit, sort });

  return queryOptions({
    queryKey: ['getApiV1DelegationMatchVacantMaster', params],
    queryFn: () => getResponse('getApiV1DelegationMatchVacantMaster', params),
  });
};

export const getApiV1RefereeWhisper = (query: string) => {
  return queryOptions({
    queryKey: ['getApiV1RefereeWhisper', query],
    queryFn: async () => getResponse('getApiV1RefereeWhisper', {
      search: query,
    }),
  });
};

export const getApiV1RefereeRegistry = (query: string) => {
  return queryOptions({
    queryKey: ['getApiV1RefereeRegistry', query],
    queryFn: async () => {
      const res = await getResponse('getApiV1RefereeRegistry', {
        search: query,
      });
      const data = res.responseData.map(item => ({
        ...item,
        id: item.userId,
      }));
      return data;
    },
  });
};

// This is a mock function, do not use it in production
export const getSeasonExcusedQueryOptionsMock = (
  userId: number | string,
  season: number | string,
  page: number = 1,
  limit: number = DEFAULT_TABLE_PAGE_SIZE,
  sort?: string,
) => {
  return queryOptions({
    queryKey: ['getSeasonExcusedQueryOptionsMock', userId, season, page, limit, sort],
    queryFn: async () => {
      return {
        statusCode: 200,
        responseData: [
          {
            inserted: '2023-10-01T10:00:00Z',
            insertedBeforeDays: 2,
            excuse: 'Onemocnění',
            matchStart: '2023-10-05T18:00:00Z',
            groupId: 101,
            groupCode: 'A1',
            homeShort: 'ABC',
            guestShort: 'XYZ',
            id: '2023-10-01T10:00:00Z',
          },
          {
            inserted: '2023-10-02T11:30:00Z',
            insertedBeforeHours: 5,
            excuse: 'Rodinné záležitosti',
            matchStart: '2023-10-06T20:00:00Z',
            groupId: 102,
            groupCode: 'B2',
            homeShort: 'DEF',
            guestShort: 'UVW',
            id: '2023-10-02T11:30:00Z',
          },
          {
            inserted: '2023-10-03T09:15:00Z',
            excuse: 'Doprava',
            matchStart: '2023-10-07T15:30:00Z',
            groupId: 103,
            groupCode: 'C3',
            homeShort: 'GHI',
            guestShort: 'RST',
            id: '2023-10-03T09:15:00Z',
          },
        ],
      };
    },
  });
};

// user rights
export const getApiV1RefereeUserRights = (params: { userId?: number; groupId?: number; matchId?: number }) => {
  params = queryParams(params);
  return queryOptions({
    queryKey: ['getApiV1RefereeUserRights', params],
    queryFn: () => getResponse('getApiV1RefereeUserRights', params),
  });
};

export const getApiV1Excuse = queryOptions({
  queryKey: ['getApiV1Excuse'],
  queryFn: () => getResponse('getApiV1Excuse', { limit: MAX_API_LIMIT }),
});

export const getApiV1RefereeDistanceHallRuianRefereeId = (refereeId: number, params: { matchId: number }, hallRuian: number) => {
  params = queryParams(params);
  return queryOptions({
    queryKey: ['getApiV1RefereeDistanceHallRuianRefereeId', hallRuian, refereeId, params],
    queryFn: () => getResponse('getApiV1RefereeDistanceHallRuianRefereeId', hallRuian, refereeId, params),
  });
};

export const getApiV1RefereeMatchesToday = (params?: GetApiV1RefereeMatchesTodayParams) => queryOptions({
  queryKey: ['getApiV1RefereeMatchesToday', params],
  queryFn: () => getResponse('getApiV1RefereeMatchesToday', params),
});
